import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { showToast } from "src/componentes";
import { AutenticacaoModule } from "src/Contexto/auth";
import { NFCeFiltrar, NFeFiltrar } from "src/services/Fiscal.Service";
import { NFCeConsultaInputDto, NFCeDto } from "src/pages/Privada/Fiscal/NFCe/NFCe.Dto";
import { NFeDto } from "src/pages/Privada/Fiscal/NFe/NFe.Dto";
import moment from "moment";
import { ApexOptions } from "apexcharts";
import { isPendenteCadastroUsuario } from "src/pages/Privada/CadastroInicial/CadastroInicial.Dto";

export interface DashboardData {
  totalFaturamento: number;
  totalNotas: number;
  totalNFe: number;
  totalNFCe: number;
  chartOptions: ApexOptions;
  chartSeries: any[];
}

const lerTema = (): string =>
  document.documentElement.getAttribute("data-bs-theme") || localStorage.getItem("theme") || "light";

export const InicioCode = () => {
  const { empresaId, userId, isLogged } = AutenticacaoModule();
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(true);
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [erroApi, setErroApi] = useState<boolean>(false);
  const [tema, setTema] = useState<string>(lerTema());

  useEffect(() => {
    const observer = new MutationObserver(() => setTema(lerTema()));
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["data-bs-theme"] });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const Load = async () => {
      if (empresaId === 0 && isLogged) {
        showToast({
          message: "Efetue o cadastro inicial de sua empresa e usuário",
          title: "Atenção",
          variant: "Warning",
        });
        navigate("/cadastro-inicial/empresa");
        return;
      }

      if (isPendenteCadastroUsuario(userId) && isLogged) {
        navigate("/minhaconta");
        return;
      }

      if (empresaId > 0) {
        await carregarDadosDashboard();
      }
    };

    Load();
  }, [empresaId, userId, isLogged]);

  const carregarDadosDashboard = async () => {
    try {
      setLoading(true);
      const dataEmissaoInicial = moment().startOf('month').toDate();
      const dataEmissaoFinal = moment().endOf('month').toDate();

      const filtro: NFCeConsultaInputDto = {
        dataEmissaoInicial,
        dataEmissaoFinal,
        codigoStatusEnvio: "2", // Finalizada/Autorizada
      };

      const [resNFCe, resNFe] = await Promise.all([
        NFCeFiltrar(filtro),
        NFeFiltrar(filtro as any)
      ]);

      let nfces: NFCeDto[] = [];
      let nfes: NFeDto[] = [];

      if (!resNFCe.data.success || !resNFe.data.success) {
        setDashboardData(null);
        setErroApi(true);
        return;
      }

      nfces = resNFCe.data.data;
      nfes = resNFe.data.data;

      setErroApi(false);
      processarDadosDashboard(nfces, nfes);
    } catch (error) {
      console.error(error);
      setDashboardData(null);
      setErroApi(true);
    } finally {
      setLoading(false);
    }
  };

  const processarDadosDashboard = (nfces: NFCeDto[], nfes: NFeDto[]) => {
    let totalFaturamentoReais = 0;
    
    const faturamentoPorDiaNFCe: Record<string, number> = {};
    const faturamentoPorDiaNFe: Record<string, number> = {};
    const diasSet = new Set<string>();

    nfces.forEach((nfce) => {
      const valor = nfce.total?.valorNotaFiscal || 0;
      totalFaturamentoReais += valor;

      if (nfce.emissaoDataHora) {
        const diaFormatado = moment(nfce.emissaoDataHora).format("DD/MM");
        faturamentoPorDiaNFCe[diaFormatado] = (faturamentoPorDiaNFCe[diaFormatado] || 0) + valor;
        diasSet.add(diaFormatado);
      }
    });

    nfes.forEach((nfe) => {
      const valor = nfe.total?.valorNotaFiscal || 0;
      totalFaturamentoReais += valor;

      if (nfe.emissaoDataHora) {
        const diaFormatado = moment(nfe.emissaoDataHora).format("DD/MM");
        faturamentoPorDiaNFe[diaFormatado] = (faturamentoPorDiaNFe[diaFormatado] || 0) + valor;
        diasSet.add(diaFormatado);
      }
    });

    const diasOrdenados = Array.from(diasSet).sort((a, b) => {
      return moment(a, "DD/MM").valueOf() - moment(b, "DD/MM").valueOf();
    });

    const seriesNFCe = diasOrdenados.map((dia) => faturamentoPorDiaNFCe[dia] || 0);
    const seriesNFe = diasOrdenados.map((dia) => faturamentoPorDiaNFe[dia] || 0);

    const chartOptions: ApexOptions = {
      chart: {
        type: 'area',
        height: 350,
        toolbar: { show: false },
        zoom: { enabled: false },
        background: 'transparent'
      },
      dataLabels: { enabled: false },
      stroke: { curve: 'smooth', width: 2 },
      xaxis: { categories: diasOrdenados },
      yaxis: {
        labels: {
          formatter: (value: number) => value.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })
        }
      },
      tooltip: {
        y: {
          formatter: (value: number) => value.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })
        }
      },
      colors: ['#0d6efd', '#17a44b'], // Azul para NFe, Verde para NFCe
      fill: {
        type: 'gradient',
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.7,
          opacityTo: 0.1,
          stops: [0, 90, 100]
        }
      },
      legend: {
        position: 'top',
        horizontalAlign: 'right'
      }
    };

    setDashboardData({
      totalFaturamento: totalFaturamentoReais,
      totalNFCe: nfces.length,
      totalNFe: nfes.length,
      totalNotas: nfces.length + nfes.length,
      chartOptions,
      chartSeries: [
        { name: "Faturamento NFe", data: seriesNFe },
        { name: "Faturamento NFCe", data: seriesNFCe }
      ]
    });
  };

  const dashboardDataComTema = useMemo<DashboardData | null>(() => {
    if (!dashboardData) return null;
    const dark = tema === "dark";
    const corTexto = dark ? "#e9ecef" : "#212529";
    return {
      ...dashboardData,
      chartOptions: {
        ...dashboardData.chartOptions,
        chart: { ...dashboardData.chartOptions.chart, foreColor: corTexto },
        legend: {
          ...dashboardData.chartOptions.legend,
          labels: { colors: corTexto }
        },
        grid: { borderColor: dark ? "#495057" : "#e0e0e0" },
        tooltip: {
          ...dashboardData.chartOptions.tooltip,
          theme: dark ? "dark" : "light"
        },
        theme: { mode: dark ? "dark" : "light" }
      }
    };
  }, [dashboardData, tema]);

  return { loading, dashboardData: dashboardDataComTema, erroApi, tema };
};
